import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Bell,
  ChevronDown,
  GraduationCap,
  Pencil,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import ELogo2 from "./assets/ELogo2.png";
import PracticeQuizArt from "./assets/practice-quiz.png";
import StudentWelcomeArt from "./assets/student-welcome.png";
import {
  CollabGroupArt,
  ExamPapersDeskArt,
  QuizThinkingArt,
  WavingStudentArt,
} from "./Components/StudentHubActivityArt";
import { useUiLanguage } from "./i18n/UiLanguageContext";
import { getPublicPracticeLibraryQuizzes, getStudentPracticeQuizzes } from "./api";
import {
  readRememberedStudentClasses,
  rememberStudentClass,
  removeRememberedStudentClass,
  removeRememberedStudentClassAccessToken,
  updateRememberedStudentClassDisplayName,
  type RememberedStudentClass,
} from "./studentClasses";
import { getStudentPracticeQuizStatus } from "./studentPracticeQuizzes";
import StudentClassUpdatesDialog, { currentClassEventCount, hasDueSoonClassEvent } from "./StudentClassUpdatesDialog";

type JoinMode = "quiz" | "collab" | "class";
type JoinResponse = {
  ok?: boolean;
  redirect_url?: string;
  token_url?: string;
  message?: string;
  session_name?: string;
};
type StudentEvent = {
  id: number;
  title: string;
  description?: string | null;
  event_date: string;
  end_date?: string | null;
  all_day: boolean;
  event_type: string;
};
const NAME_KEY = "elume_student_name_v1";
const COLOUR: Record<string, { card: string; dot: string; action: string }> = {
  violet: {
    card: "border-violet-200 bg-violet-50",
    dot: "bg-violet-500",
    action: "bg-violet-700 hover:bg-violet-800",
  },
  sky: {
    card: "border-sky-200 bg-sky-50",
    dot: "bg-sky-500",
    action: "bg-sky-700 hover:bg-sky-800",
  },
  emerald: {
    card: "border-emerald-200 bg-emerald-50",
    dot: "bg-emerald-500",
    action: "bg-emerald-700 hover:bg-emerald-800",
  },
  rose: {
    card: "border-rose-200 bg-rose-50",
    dot: "bg-rose-500",
    action: "bg-rose-700 hover:bg-rose-800",
  },
  amber: {
    card: "border-amber-200 bg-amber-50",
    dot: "bg-amber-500",
    action: "bg-amber-700 hover:bg-amber-800",
  },
  indigo: {
    card: "border-indigo-200 bg-indigo-50",
    dot: "bg-indigo-500",
    action: "bg-indigo-700 hover:bg-indigo-800",
  },
  teal: {
    card: "border-teal-200 bg-teal-50",
    dot: "bg-teal-500",
    action: "bg-teal-700 hover:bg-teal-800",
  },
  fuchsia: {
    card: "border-fuchsia-200 bg-fuchsia-50",
    dot: "bg-fuchsia-500",
    action: "bg-fuchsia-700 hover:bg-fuchsia-800",
  },
  lime: {
    card: "border-lime-200 bg-lime-50",
    dot: "bg-lime-500",
    action: "bg-lime-700 hover:bg-lime-800",
  },
  orange: {
    card: "border-orange-200 bg-orange-50",
    dot: "bg-orange-500",
    action: "bg-orange-700 hover:bg-orange-800",
  },
  cyan: {
    card: "border-cyan-200 bg-cyan-50",
    dot: "bg-cyan-500",
    action: "bg-cyan-700 hover:bg-cyan-800",
  },
  pink: {
    card: "border-pink-200 bg-pink-50",
    dot: "bg-pink-500",
    action: "bg-pink-700 hover:bg-pink-800",
  },
};
function cleanCode(value: string) {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 8);
}
function cleanPin(value: string) {
  return value.replace(/\D/g, "").slice(0, 6);
}
function readName() {
  try {
    return localStorage.getItem(NAME_KEY)?.trim() || "";
  } catch {
    return "";
  }
}
function writeName(value: string) {
  try {
    localStorage.setItem(NAME_KEY, value);
  } catch {
    /* storage can be restricted */
  }
}
function DecorativeGrid() {
  return (
    <div
      className="pointer-events-none absolute inset-0 opacity-[.08] [background-image:linear-gradient(to_right,#475569_1px,transparent_1px),linear-gradient(to_bottom,#475569_1px,transparent_1px)] [background-size:30px_30px]"
      aria-hidden="true"
    />
  );
}
type LiveActivityCardProps = {
  liveLabel: string;
  title: string;
  description: string;
  action: string;
  artwork: React.ReactNode;
  tone: "quiz" | "collab";
  onClick: () => void;
};
type PracticeQuiz = {
  id: number;
  classId: number;
  scope?: "class" | "global";
  title: string;
  className: string;
  subject?: string;
  level?: string;
  topic?: string;
  questions: number;
  duration?: string;
  accessToken?: string;
  fixtureStatus?: "New" | "In progress" | "Completed";
};
function practicePrototypeEnabled() {
  return process.env.REACT_APP_STUDENT_HUB_PRACTICE_PROTOTYPE === "true";
}
const PRACTICE_FIXTURE_DATA: PracticeQuiz[] = [
  {
    id: 1,
    classId: 1,
    title: "Algebra Basics",
    className: "5th Year Maths",
    questions: 10,
    duration: "~15 mins",
    fixtureStatus: "New",
  },
  {
    id: 2,
    classId: 1,
    title: "Complex Numbers Check",
    className: "6th Year Maths",
    questions: 8,
    duration: "~12 mins",
    fixtureStatus: "In progress",
  },
  {
    id: 3,
    classId: 2,
    title: "Forces Revision",
    className: "6th Year Physics",
    questions: 12,
    duration: "~20 mins",
    fixtureStatus: "Completed",
  },
];
function LiveActivityCard({
  liveLabel,
  title,
  description,
  action,
  artwork,
  tone,
  onClick,
}: LiveActivityCardProps) {
  const isQuiz = tone === "quiz";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative min-h-[230px] overflow-hidden rounded-[30px] p-6 text-left text-white shadow-[0_20px_40px_rgba(15,23,42,.22)] focus:outline-none focus:ring-4 ${isQuiz ? "bg-[linear-gradient(135deg,#5b21b6,#7c3aed_55%,#a855f7)] focus:ring-violet-300" : "bg-[linear-gradient(135deg,#ea580c,#f97316_56%,#fb923c)] focus:ring-orange-300"}`}
    >
      <div className="relative z-10 grid h-full min-h-[182px] grid-cols-[minmax(0,1fr)_7.25rem] grid-rows-[auto_auto_minmax(0,1fr)_auto] gap-x-3 sm:grid-cols-[minmax(0,1fr)_14rem]">
        <div className="col-start-1 row-start-1 inline-flex h-9 w-fit items-center gap-2 rounded-full bg-white/20 px-3 text-lg font-black uppercase tracking-[.08em] text-white backdrop-blur-sm sm:text-xl">
          <span className="h-2 w-2 rounded-full bg-white" />
          {liveLabel}
        </div>
        <h2 className="col-start-1 row-start-2 mt-3 text-3xl font-black leading-none">
          {title}
        </h2>
        <p
          className={`col-start-1 row-start-3 mt-2 text-sm leading-6 ${isQuiz ? "text-violet-100" : "text-orange-100"}`}
        >
          {description}
        </p>
        <span
          className={`col-start-1 row-start-4 mt-5 inline-flex w-fit items-center gap-2 self-end rounded-2xl bg-white px-4 py-3 font-black ${isQuiz ? "text-violet-800" : "text-orange-800"}`}
        >
          {action}
          <ArrowRight size={17} />
        </span>
        <div className="pointer-events-none col-start-2 row-span-4 row-start-1 flex items-end justify-end">
          {artwork}
        </div>
      </div>
    </button>
  );
}
function PracticeQuizRows({
  quizzes,
  compact = false,
  loading = false,
  loadingMessage = "Loading practice quizzes…",
  emptyMessage = "No practice quizzes available yet.",
  onOpen,
}: {
  quizzes: PracticeQuiz[];
  compact?: boolean;
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  onOpen: (quiz: PracticeQuiz) => void;
}) {
  if (loading)
    return (
      <p className="rounded-2xl bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-500">
        {loadingMessage}
      </p>
    );
  if (!quizzes.length)
    return (
      <p className="rounded-2xl bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-600">
        {emptyMessage}
      </p>
    );
  return (
    <div className={compact ? "divide-y divide-slate-100" : "mt-4 space-y-3"}>
      {quizzes.map((quiz) => {
        const status =
          quiz.fixtureStatus ||
          getStudentPracticeQuizStatus(quiz.classId, quiz.id, quiz.scope || "class");
        const action =
          status === "Completed"
            ? "View results"
            : status === "In progress"
              ? "Continue"
              : "Start";
        const badge =
          status === "Completed"
            ? "bg-emerald-100 text-emerald-800"
            : status === "In progress"
              ? "bg-amber-100 text-amber-900"
              : "bg-pink-100 text-pink-800";
        const context = [
          quiz.scope === "global" ? "Elume Practice" : "",
          quiz.className,
          quiz.subject,
          `${quiz.questions} questions`,
          quiz.duration,
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <div
            key={`${quiz.scope || "class"}:${quiz.classId}:${quiz.id}`}
            className={
              compact
                ? "flex min-h-[70px] items-center gap-3 py-3"
                : "flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
            }
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-black text-slate-900">{quiz.title}</p>
              <p className="mt-0.5 truncate text-xs font-semibold text-slate-500">
                {context}
              </p>
            </div>
            <span
              className={`hidden rounded-full px-2.5 py-1 text-xs font-black sm:inline ${badge}`}
            >
              {status}
            </span>
            <button
              type="button"
              disabled={!quiz.accessToken}
              onClick={() => onOpen(quiz)}
              aria-label={`${action}: ${quiz.title}`}
              className="shrink-0 rounded-xl bg-rose-500 px-3 py-2 text-xs font-black text-white shadow-sm transition-colors hover:bg-rose-600 focus:outline-none focus:ring-4 focus:ring-rose-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {action}
              <ArrowRight className="ml-1 inline" size={13} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
function PracticeLibraryEntry({
  quizzes,
  compact = false,
  featured = false,
  title = "Elume Practice Library",
  help = "Explore practice by collection and topic",
  action = "Explore",
}: {
  quizzes: PracticeQuiz[];
  compact?: boolean;
  featured?: boolean;
  title?: string;
  help?: string;
  action?: string;
}) {
  const collections = new Set(quizzes.map((quiz) => `${quiz.level || "Other"} ${quiz.subject === "Mathematics" ? "Maths" : quiz.subject || "Practice"}`));
  const count = !featured && collections.size ? ` · ${quizzes.length} quizzes available` : "";
  return <button type="button" onClick={() => { window.location.hash = "/student/practice/library"; }} className={`${featured ? "" : "mt-4"} w-full rounded-2xl border p-4 text-left shadow-sm focus:outline-none focus:ring-4 ${featured ? "border-rose-200 bg-[linear-gradient(135deg,#fff1f2,#fdf4ff)] focus:ring-rose-200 sm:p-5" : `border-violet-200 bg-[linear-gradient(135deg,#f5f3ff,#fdf4ff)] focus:ring-violet-200 ${compact ? "" : "sm:p-5"}`}`}><div className="flex items-center gap-3"><span className={`grid h-10 w-10 shrink-0 place-items-center rounded-2xl text-white ${featured ? "bg-rose-500" : "bg-violet-600"}`}><Sparkles size={19} /></span><span className="min-w-0 flex-1"><span className={`block text-sm font-black ${featured ? "text-rose-950" : "text-violet-950"}`}>{title}</span><span className="mt-0.5 block text-xs font-semibold text-slate-600">{help}{count}</span><span className={`mt-3 inline-flex min-h-10 items-center rounded-xl px-3 text-sm font-black text-white ${featured ? "bg-rose-600" : "bg-violet-600"}`}>{action}<ArrowRight className="ml-2" size={16} /></span></span></div></button>;
}
export default function StudentPage() {
  const { t } = useUiLanguage();
  const [name, setName] = useState("");
  const [draft, setDraft] = useState("");
  const [welcome, setWelcome] = useState(false);
  const [changing, setChanging] = useState(false);
  const [classes, setClasses] = useState<RememberedStudentClass[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [joinMode, setJoinMode] = useState<JoinMode | null>(null);
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [joining, setJoining] = useState(false);
  const [events, setEvents] = useState<Record<string, StudentEvent[]>>({});
  const [classUpdates, setClassUpdates] = useState<{ item: RememberedStudentClass } | null>(null);
  const [globalUpdates, setGlobalUpdates] = useState<{ groups: { className: string; events: StudentEvent[] }[] } | null>(null);
  const classUpdateBell = useRef<HTMLButtonElement>(null);
  const globalUpdateBell = useRef<HTMLButtonElement>(null);
  const nameInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const saved = readName();
    setName(saved);
    setDraft(saved);
    setWelcome(saved.length < 2);
    const remembered = readRememberedStudentClasses();
    setClasses(remembered);
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    if (mode === "quiz" || mode === "collab" || mode === "class") {
      setJoinMode(mode);
      setCode(cleanCode(params.get("code") || ""));
    }
  }, []);
  useEffect(() => {
    if (welcome || changing)
      window.setTimeout(() => nameInput.current?.focus(), 0);
  }, [welcome, changing]);
  useEffect(() => {
    let cancelled = false;
    const available = classes.filter((item) => item.accessToken).slice(0, showAll ? 12 : 4);
    if (!available.length) {
      setEvents({});
      return;
    }
    Promise.all(
      available.map(async (item) => {
        try {
          const response = await fetch(
            `/api/student/${item.accessToken}/events`,
          );
          if (!response.ok) throw new Error();
          const body = await response.json();
          return [item.classCode, Array.isArray(body) ? body : []] as const;
        } catch {
          return [item.classCode, []] as const;
        }
      }),
    )
      .then((rows) => {
        if (!cancelled) {
          setEvents(Object.fromEntries(rows));
        }
      })
    return () => {
      cancelled = true;
    };
  }, [classes, showAll]);
  const [practiceQuizzes, setPracticeQuizzes] = useState<PracticeQuiz[]>([]);
  const [globalPracticeQuizzes, setGlobalPracticeQuizzes] = useState<PracticeQuiz[]>([]);
  const [practiceState, setPracticeState] = useState<"loading" | "ready">(
    "loading",
  );
  const showPracticeFixtures = practicePrototypeEnabled();
  useEffect(() => {
    let cancelled = false;
    if (showPracticeFixtures) {
      setPracticeQuizzes(PRACTICE_FIXTURE_DATA);
      setPracticeState("ready");
      return;
    }
    const available = classes.filter((item) => item.accessToken);
    if (!available.length) {
      setPracticeQuizzes([]);
      setPracticeState("ready");
      return;
    }
    setPracticeState("loading");
    Promise.all(
      available.map(async (item) => {
        try {
          const rows = await getStudentPracticeQuizzes(item.accessToken!);
          return rows
            .map((quiz) => ({
              id: Number(quiz.id),
              classId: Number(quiz.class_id),
              title: String(quiz.title || "").trim(),
              scope: (quiz.scope === "global" ? "global" : "class") as "class" | "global",
              className: quiz.scope === "global" ? "Elume Practice" : String(
                quiz.class_name ||
                  item.displayName ||
                  item.className ||
                  item.classCode,
              ),
              subject: quiz.subject || item.subject,
              level: quiz.level || undefined,
              topic: quiz.topic || undefined,
              questions: Math.max(0, Number(quiz.question_count) || 0),
              accessToken: item.accessToken,
            }))
            .filter((quiz) => quiz.id > 0 && quiz.classId > 0 && quiz.title);
        } catch (error: any) {
          if (error?.status === 404)
            removeRememberedStudentClassAccessToken(item.accessToken!);
          return [] as PracticeQuiz[];
        }
      }),
    )
      .then((rows) => {
        if (cancelled) return;
        const deduped = new Map<string, PracticeQuiz>();
        rows.flat().forEach((quiz) => deduped.set(`${quiz.scope}:${quiz.classId}:${quiz.id}`, quiz));
        setPracticeQuizzes(Array.from(deduped.values()));
        setPracticeState("ready");
        if (
          available.some(
            (item) =>
              !readRememberedStudentClasses().some(
                (saved) => saved.accessToken === item.accessToken,
              ),
          )
        )
          setClasses(readRememberedStudentClasses());
      })
      .catch(() => !cancelled && setPracticeState("ready"));
    return () => {
      cancelled = true;
    };
  }, [classes, showPracticeFixtures]);
  useEffect(() => {
    let cancelled = false;
    if (showPracticeFixtures) {
      setGlobalPracticeQuizzes([]);
      return;
    }
    getPublicPracticeLibraryQuizzes()
      .then((rows) => {
        if (cancelled) return;
        setGlobalPracticeQuizzes(
          rows
            .filter((quiz) => quiz.scope === "global")
            .map((quiz) => ({
              id: Number(quiz.id),
              classId: Number(quiz.class_id),
              title: String(quiz.title || "").trim(),
              scope: "global" as const,
              className: "Elume Practice",
              subject: quiz.subject || undefined,
              level: quiz.level || undefined,
              topic: quiz.topic || undefined,
              questions: Math.max(0, Number(quiz.question_count) || 0),
            }))
            .filter((quiz) => quiz.id > 0 && quiz.title),
        );
      })
      .catch(() => !cancelled && setGlobalPracticeQuizzes([]));
    return () => {
      cancelled = true;
    };
  }, [showPracticeFixtures]);
  const displayedPracticeQuizzes = showPracticeFixtures
    ? PRACTICE_FIXTURE_DATA
    : practiceQuizzes;
  const classPracticeQuizzes = displayedPracticeQuizzes.filter((quiz) => (quiz.scope || "class") === "class");
  const nameReady = name.trim().length >= 2;
  const shown = showAll ? classes : classes.slice(0, 4);
  const upcoming = useMemo(
    () =>
      Object.values(events)
        .flat()
        .filter(
          (event) =>
            new Date(event.event_date).getTime() >= Date.now() - 86400000,
        )
        .sort((a, b) => a.event_date.localeCompare(b.event_date))
        .slice(0, 4),
    [events],
  );
  const globalUpdateCount = upcoming.length;
  const globalSoon = hasDueSoonClassEvent(upcoming);
  function saveName() {
    const clean = draft.trim().slice(0, 40);
    if (clean.length < 2) return;
    writeName(clean);
    setName(clean);
    setWelcome(false);
    setChanging(false);
  }
  function openJoin(mode: JoinMode) {
    setError("");
    setCode("");
    setPin("");
    setJoinMode(mode);
  }
  async function submit() {
    if (!joinMode) return;
    if (!nameReady) {
      setWelcome(true);
      return;
    }
    if (!code) {
      setError(t("studentHub.codeRequired"));
      return;
    }
    if (joinMode !== "class") {
      window.location.href =
        joinMode === "quiz"
          ? `${window.location.origin}/#/join/${code}`
          : `${window.location.origin}/#/collab/join/${code}`;
      return;
    }
    if (!pin) {
      setError(t("studentHub.pinRequired"));
      return;
    }
    setJoining(true);
    setError("");
    try {
      const response = await fetch("/api/student/join/class", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, pin, name }),
      });
      const data = (await response.json().catch(() => ({}))) as JoinResponse;
      if (!response.ok || !data.ok)
        throw new Error(data.message || t("studentHub.joinFailed"));
      const target = data.redirect_url || data.token_url || "";
      const token = target.match(/^\/?student\/([A-Za-z0-9_-]{16,128})$/)?.[1];
      rememberStudentClass({
        classCode: code,
        accessToken: token,
        className: data.session_name,
      });
      setClasses(readRememberedStudentClasses());
      window.location.href = /^https?:/i.test(target)
        ? target
        : `${window.location.origin}/#/${target.replace(/^\//, "")}`;
    } catch (e: any) {
      setError(e?.message || t("studentHub.joinFailed"));
    } finally {
      setJoining(false);
    }
  }
  function openClass(item: RememberedStudentClass) {
    if (item.accessToken) {
      window.location.href = `${window.location.origin}/#/student/${item.accessToken}`;
      return;
    }
    setJoinMode("class");
    setCode(item.classCode);
    setPin("");
    setError(t("studentHub.enterPin"));
  }
  function openClassUpdates(item: RememberedStudentClass) { setClassUpdates({ item }); }
  function openGlobalUpdates() {
    const available = classes.filter((item) => item.accessToken).slice(0, 12);
    setGlobalUpdates({ groups: available.map((item) => ({ className: item.displayName || item.className || item.classCode, events: events[item.classCode] || [] })) });
  }
  function openPracticeQuiz(quiz: PracticeQuiz) {
    if (!quiz.accessToken) return;
    const scope = quiz.scope || "class";
    window.location.hash = `/student/${encodeURIComponent(quiz.accessToken)}/practice-quiz/${quiz.id}?scope=${scope}${scope === "class" ? `&classId=${quiz.classId}` : ""}`;
  }
  function editClass(item: RememberedStudentClass) {
    const next = window.prompt(
      t("studentHub.editClassName"),
      item.displayName || item.className || "",
    );
    if (next?.trim()) {
      updateRememberedStudentClassDisplayName(item.classCode, next);
      setClasses(readRememberedStudentClasses());
    }
  }
  const title =
    joinMode === "quiz"
      ? t("studentHub.liveQuiz")
      : joinMode === "collab"
        ? t("studentHub.collabBoard")
        : t("studentHub.joinAnotherClass");
  const codeLabel =
    joinMode === "quiz"
      ? t("studentHub.quizCode")
      : joinMode === "collab"
        ? t("studentHub.boardCode")
        : t("studentHub.classCode");
  return (
    <div className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_10%_0%,rgba(250,204,21,.38),transparent_26%),radial-gradient(circle_at_100%_20%,rgba(139,92,246,.18),transparent_30%),linear-gradient(180deg,#fffdf3_0%,#f8f6ff_48%,#f7fbff_100%)] text-slate-900">
      <DecorativeGrid />
      <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <a
          href="/#/student"
          className="inline-flex items-center gap-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-violet-300"
        >
          <img src={ELogo2} alt="Elume" className="h-10 w-10 object-contain" />
          <span className="text-sm font-black tracking-tight">Elume</span>
        </a>
        <div className="rounded-full bg-white/80 px-3 py-1.5 text-xs font-bold text-slate-600 shadow-sm ring-1 ring-slate-200">
          {t("studentHub.studentHub")}
        </div>
      </header>
      <main className="relative z-10 mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        <section className="relative overflow-hidden rounded-[32px] border border-yellow-300 bg-[linear-gradient(110deg,#fff36a,#ffd447)] px-5 py-7 shadow-[0_20px_50px_rgba(234,179,8,.18)] sm:px-8 sm:py-9">
          <div className="relative z-10 max-w-[70%] sm:max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-xs font-black tracking-[.18em]">
              <GraduationCap size={16} />
              {t("studentHub.eyebrow")}
            </div>
            <h1 className="mt-4 text-4xl font-black tracking-tight sm:text-5xl">
              {nameReady
                ? t("studentHub.welcomeName", { name })
                : t("studentHub.welcome")}
            </h1>
            <p className="mt-3 max-w-2xl text-sm font-medium leading-6 sm:text-base">
              {t("studentHub.headingHelp")}
            </p>
          </div>
          <img
            src={StudentWelcomeArt}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 right-2 h-[118px] max-w-[34%] object-contain sm:right-6 sm:h-[160px] lg:h-[185px]"
          />
        </section>
        <section className="mt-6" aria-labelledby="saved-classes-heading">
          <div className="mb-3 flex items-end justify-between gap-3">
            <div>
              <h2
                id="saved-classes-heading"
                className="text-2xl font-black tracking-tight"
              >
                {t("studentHub.savedClasses")}
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                {t("studentHub.savedClassesHelp")}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button ref={globalUpdateBell} type="button" aria-label={`View all class updates${globalUpdateCount ? ` (${globalUpdateCount} current)` : ""}`} onClick={openGlobalUpdates} className={`relative grid min-h-11 min-w-11 place-items-center rounded-2xl border shadow-sm focus:outline-none focus:ring-4 focus:ring-sky-200 ${globalSoon ? "border-amber-300 bg-amber-100 text-amber-900" : globalUpdateCount ? "border-sky-200 bg-sky-100 text-sky-800" : "border-slate-200 bg-white text-slate-600"}`}><Bell size={18} />{globalUpdateCount ? <span aria-hidden="true" className={`absolute -right-1 -top-1 min-w-5 rounded-full px-1 text-center text-[10px] font-black leading-5 text-white ${globalSoon ? "bg-amber-600" : "bg-slate-900"}`}>{globalUpdateCount}</span> : null}</button>
              <button type="button" onClick={() => openJoin("class")} className="rounded-2xl bg-white px-4 py-3 text-sm font-black shadow-sm ring-1 ring-slate-200 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-violet-300"><Plus className="mr-1 inline" size={16} />{t("studentHub.joinAnotherClass")}</button>
            </div>
          </div>
          {classes.length === 0 ? (
            <button
              type="button"
              onClick={() => openJoin("class")}
              className="flex w-full items-center justify-between rounded-[26px] border-2 border-dashed border-violet-300 bg-white/80 p-5 text-left shadow-sm hover:border-violet-500"
            >
              <span>
                <span className="block text-lg font-black">
                  {t("studentHub.addFirstClass")}
                </span>
                <span className="mt-1 block text-sm text-slate-600">
                  {t("studentHub.addFirstClassHelp")}
                </span>
              </span>
              <ArrowRight className="text-violet-700" />
            </button>
          ) : (
            <div className="flex snap-x gap-3 overflow-x-auto pb-2 sm:grid sm:grid-cols-2 sm:overflow-visible xl:grid-cols-4">
              {shown.map((item) => {
                const style =
                  COLOUR[item.colourId || "violet"] || COLOUR.violet;
                return (
                  <article
                    key={item.classCode}
                    className={`w-[136px] shrink-0 snap-start rounded-[20px] border p-3 shadow-sm sm:w-auto sm:rounded-[24px] sm:p-4 ${style.card}`}
                  >
                    {(() => {
                      const count = currentClassEventCount(events[item.classCode] || []);
                      const dueSoon = hasDueSoonClassEvent(events[item.classCode] || []);
                      return <button ref={classUpdateBell} type="button" aria-label={`View ${item.displayName || item.className || item.classCode} updates${count ? ` (${count} current)` : ""}`} onClick={(event) => { event.stopPropagation(); openClassUpdates(item); }} className={`relative float-right grid min-h-9 min-w-9 place-items-center rounded-xl border focus:outline-none focus:ring-4 focus:ring-sky-200 ${dueSoon ? "border-amber-300 bg-amber-100 text-amber-900" : count ? "border-sky-200 bg-sky-100 text-sky-800" : "border-slate-200 bg-white/70 text-slate-500"}`}><Bell size={16} />{count ? <span aria-hidden="true" className={`absolute -right-1 -top-1 min-w-4 rounded-full px-1 text-[9px] font-black leading-4 text-white ${dueSoon ? "bg-amber-600" : "bg-slate-900"}`}>{count}</span> : null}</button>;
                    })()}
                    <button
                      type="button"
                      onClick={() => openClass(item)}
                      className="w-full text-left focus:outline-none focus:ring-4 focus:ring-violet-300"
                    >
                      <span
                        className={`mb-2 block h-2.5 w-2.5 sm:mb-4 sm:h-3 sm:w-3 rounded-full ${style.dot}`}
                      />
                      <span className="block truncate text-sm font-black sm:text-lg">
                        {item.displayName ||
                          item.className ||
                          t("studentHub.classFallback")}
                      </span>
                      <span className="mt-1 block truncate text-[10px] font-bold uppercase tracking-[.08em] sm:text-xs sm:tracking-[.12em] text-slate-600">
                        {item.subject || t("studentHub.classCode")} ·{" "}
                        {item.classCode}
                      </span>
                      <span
                        className={`mt-2 inline-flex rounded-lg px-2 py-1 text-xs font-black sm:mt-4 sm:rounded-xl sm:px-3 sm:py-2 sm:text-sm text-white ${style.action}`}
                      >
                        {t("common.open")}
                      </span>
                    </button>
                    <div className="mt-2 flex gap-2 text-[10px] font-semibold text-slate-600 sm:mt-3 sm:gap-3 sm:text-xs">
                      <button
                        type="button"
                        onClick={() => editClass(item)}
                        className="underline hover:text-slate-900"
                      >
                        <Pencil className="mr-1 inline" size={12} />
                        {t("common.edit")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          removeRememberedStudentClass(item.classCode);
                          setClasses(readRememberedStudentClasses());
                        }}
                        className="underline hover:text-slate-900"
                      >
                        {t("studentHub.removeDevice")}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
          {classes.length > 4 && (
            <button
              type="button"
              onClick={() => setShowAll((value) => !value)}
              className="mt-4 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold text-violet-800 hover:bg-violet-50"
            >
              {showAll ? t("studentHub.showLess") : t("studentHub.viewAll")}
              <ChevronDown className={showAll ? "rotate-180" : ""} size={16} />
            </button>
          )}
        </section>
        <section className="mt-5 lg:hidden" aria-label={t("studentHub.practiceQuizzes")}>
          <PracticeLibraryEntry
            featured
            quizzes={globalPracticeQuizzes}
            title={t("studentHub.practiceQuizzes")}
            help={t("studentHub.practiceLibraryHelp")}
            action={t("studentHub.openPracticeLibrary")}
          />
        </section>
        <section
          className="mt-5 grid gap-5 lg:grid-cols-2"
          aria-label={t("studentHub.liveActivities")}
        >
          <LiveActivityCard
            liveLabel={t("studentHub.live")}
            tone="quiz"
            title={t("studentHub.liveQuiz")}
            description={t("studentHub.quizHelp")}
            action={t("studentHub.enterQuizCode")}
            artwork={<QuizThinkingArt />}
            onClick={() => openJoin("quiz")}
          />
          <div
            className="relative hidden min-h-[230px] overflow-hidden rounded-[30px] bg-[linear-gradient(135deg,#e11d48,#fb7185_55%,#fb923c)] p-6 text-left text-white shadow-[0_20px_40px_rgba(225,29,72,.22)] focus:outline-none focus:ring-4 focus:ring-pink-300 lg:block"
          >
            <div className="relative z-10 max-w-[58%]">
              <p className="inline-flex rounded-full bg-white/20 px-3 py-2 text-sm font-black tracking-[.12em]">
                SELF-PACED
              </p>
              <h2 className="mt-3 text-3xl font-black leading-none">
                {t("studentHub.practiceQuizzes")}
              </h2>
              <p className="mt-3 text-sm leading-6 text-pink-50">
                Complete quizzes from your teacher anytime and get instant
                feedback.
              </p>
              <button
                type="button"
                onClick={() => {
                  window.location.hash = "/student/practice/library";
                }}
                className="mt-5 inline-flex rounded-2xl bg-white px-4 py-3 font-black text-pink-800 focus:outline-none focus:ring-4 focus:ring-pink-300"
              >
                {t("studentHub.openPracticeLibrary")}
                <ArrowRight className="ml-2" size={17} />
              </button>
            </div>
            <img
              src={PracticeQuizArt}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute bottom-2 right-2 h-[82%] max-w-[45%] object-contain"
            />
          </div>
          <LiveActivityCard
            liveLabel={t("studentHub.live")}
            tone="collab"
            title={t("studentHub.collabBoard")}
            description={t("studentHub.collabHelp")}
            action={t("studentHub.enterBoardCode")}
            artwork={<CollabGroupArt />}
            onClick={() => openJoin("collab")}
          />
          <button
            type="button"
            onClick={() => {
              window.location.href = `${window.location.origin}/#/student/exam-papers`;
            }}
            className="relative min-h-[215px] overflow-hidden rounded-[28px] bg-[linear-gradient(135deg,#0758e8,#1677f3_58%,#38a7f8)] p-6 text-left text-white shadow-[0_20px_40px_rgba(7,88,232,.24)] focus:outline-none focus:ring-4 focus:ring-sky-300 sm:min-h-[205px]"
          >
            <div className="relative z-10 max-w-[10rem] sm:max-w-[21rem]">
              <h2 className="text-3xl font-black tracking-tight">
                {t("studentHub.examPapers")}
              </h2>
              <p className="mt-2 text-sm leading-6 text-sky-50">
                {t("studentHub.examPapersHelp")}
              </p>
              <span className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-3 font-black text-blue-800">
                {t("studentHub.browsePapers")}
                <ArrowRight size={17} />
              </span>
            </div>
            <div className="pointer-events-none absolute bottom-1 right-2">
              <ExamPapersDeskArt />
            </div>
          </button>
        </section>
        <section
          className="mt-6 rounded-[24px] border border-rose-200 bg-rose-50/85 p-4 shadow-sm lg:hidden"
          aria-labelledby="practice-mobile-heading"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <img src={PracticeQuizArt} alt="" aria-hidden="true" className="h-9 w-9 shrink-0 object-contain" />
              <div>
              <h2 id="practice-mobile-heading" className="text-xl font-black text-rose-950">{t("studentHub.teacherQuizzes")}</h2>
              <p className="mt-1 text-sm leading-5 text-slate-600">
                {t("studentHub.teacherQuizzesHelp")}
              </p>
              </div>
            </div>
            <span className="shrink-0 rounded-full bg-rose-200 px-2.5 py-1 text-xs font-black text-rose-900">
              {classPracticeQuizzes.length}
            </span>
          </div>
          {classPracticeQuizzes.length > 0 && <PracticeQuizRows compact quizzes={classPracticeQuizzes} onOpen={openPracticeQuiz} loading={false} loadingMessage={t("studentHub.loadingTeacherQuizzes")} emptyMessage={t("studentHub.noTeacherQuizzes")} />}
          {!classPracticeQuizzes.length && <PracticeQuizRows compact quizzes={[]} onOpen={openPracticeQuiz} loading={practiceState === "loading" && !showPracticeFixtures} loadingMessage={t("studentHub.loadingTeacherQuizzes")} emptyMessage={t("studentHub.noTeacherQuizzes")} />}
        </section>
        <section
          className="mt-6 hidden lg:block"
          aria-labelledby="available-quizzes-heading"
        >
          <div className="mb-3 flex items-end justify-between">
            <div className="flex items-end gap-3">
              <img src={PracticeQuizArt} alt="" aria-hidden="true" className="h-11 w-11 object-contain" />
              <div>
              <p className="text-xs font-black tracking-[.16em] text-pink-600">
                SELF-PACED
              </p>
              <h2
                id="available-quizzes-heading"
                className="mt-1 text-2xl font-black"
              >
                {t("studentHub.practiceQuizzes")}
              </h2>
              </div>
            </div>
            <span className="rounded-full bg-pink-100 px-3 py-1 text-xs font-black text-pink-800">
              {practiceState === "loading" && !showPracticeFixtures
                ? "Loading"
                : displayedPracticeQuizzes.length
                  ? `${displayedPracticeQuizzes.length} available`
                  : "Self-paced"}
            </span>
          </div>
          <div className="rounded-[28px] border border-pink-100 bg-pink-50/50 p-5">
            <p className="max-w-xl text-sm text-slate-600">
              Complete quizzes from your teacher anytime and get instant
              feedback.
            </p>
            <PracticeLibraryEntry quizzes={globalPracticeQuizzes} title={t("studentHub.practiceLibrary")} help={t("studentHub.practiceLibraryHelp")} action={t("studentHub.openPracticeLibrary")} />
            {classPracticeQuizzes.length > 0 && <><h3 className="mt-4 text-sm font-black text-rose-950">{t("studentHub.teacherQuizzes")}</h3><PracticeQuizRows quizzes={classPracticeQuizzes} onOpen={openPracticeQuiz} loading={false} loadingMessage={t("studentHub.loadingTeacherQuizzes")} emptyMessage={t("studentHub.noTeacherQuizzes")} /></>}
            {!classPracticeQuizzes.length && <><h3 className="mt-4 text-sm font-black text-rose-950">{t("studentHub.teacherQuizzes")}</h3><PracticeQuizRows quizzes={[]} onOpen={openPracticeQuiz} loading={practiceState === "loading" && !showPracticeFixtures} loadingMessage={t("studentHub.loadingTeacherQuizzes")} emptyMessage={t("studentHub.noTeacherQuizzes")} /></>}
          </div>
        </section>
        <section className="mt-6 flex items-center justify-between rounded-[24px] border border-slate-200 bg-white/80 px-5 py-4">
          <div>
            <p className="text-sm text-slate-600">{t("studentHub.nickname")}</p>
            <p className="text-lg font-black">
              {nameReady ? name : t("studentHub.noNickname")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setDraft(name);
              setChanging(true);
            }}
            className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-black hover:bg-slate-50"
          >
            {t("studentHub.changeName")}
          </button>
        </section>
      </main>
      {(welcome || changing) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="student-name-title"
        >
          <div className="w-full max-w-md rounded-[30px] bg-white p-6 shadow-2xl">
            <WavingStudentArt />
            <h2
              id="student-name-title"
              className="mt-1 text-center text-2xl font-black"
            >
              {t("studentHub.nameWelcome")}
            </h2>
            <p className="mt-2 text-center text-sm leading-6 text-slate-600">
              {t("studentHub.nameHelp")}
            </p>
            <label
              htmlFor="student-nickname"
              className="mt-5 block text-sm font-bold"
            >
              {t("studentHub.nickname")}
            </label>
            <input
              ref={nameInput}
              id="student-nickname"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") saveName();
              }}
              placeholder={t("studentHub.nicknamePlaceholder")}
              maxLength={40}
              className="mt-2 w-full rounded-2xl border-2 border-slate-200 px-4 py-3 text-lg font-semibold outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
            />
            <button
              type="button"
              disabled={draft.trim().length < 2}
              onClick={saveName}
              className="mt-4 w-full rounded-2xl bg-slate-900 px-4 py-3 font-black text-white disabled:opacity-40"
            >
              {t("studentHub.continue")}
            </button>
            {changing && (
              <button
                type="button"
                onClick={() => setChanging(false)}
                className="mt-3 w-full text-sm font-bold text-slate-600 underline"
              >
                {t("common.cancel")}
              </button>
            )}
          </div>
        </div>
      )}
      {joinMode && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-slate-950/45 sm:items-center sm:justify-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="join-title"
        >
          <div className="w-full max-w-lg rounded-t-[30px] bg-white p-6 shadow-2xl sm:rounded-[30px]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="join-title" className="text-2xl font-black">
                  {title}
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  {joinMode === "class"
                    ? t("studentHub.classJoinHelp")
                    : joinMode === "quiz"
                      ? t("studentHub.quizJoinHelp")
                      : t("studentHub.collabJoinHelp")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setJoinMode(null)}
                className="rounded-xl p-2 hover:bg-slate-100"
                aria-label={t("common.close")}
              >
                <X />
              </button>
            </div>
            <label
              htmlFor="student-join-code"
              className="mt-5 block text-sm font-bold"
            >
              {codeLabel}
            </label>
            <input
              id="student-join-code"
              aria-label={codeLabel}
              value={code}
              onChange={(event) => setCode(cleanCode(event.target.value))}
              onKeyDown={(event) => {
                if (event.key === "Enter") submit();
              }}
              placeholder={
                joinMode === "class"
                  ? t("studentHub.classCodePlaceholder")
                  : joinMode === "quiz"
                    ? t("studentHub.quizCodePlaceholder")
                    : t("studentHub.boardCodePlaceholder")
              }
              className="mt-2 w-full rounded-2xl border-2 border-slate-200 px-4 py-3 text-lg font-bold tracking-[.12em] outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
            />
            {joinMode === "class" && (
              <>
                <label
                  htmlFor="student-join-pin"
                  className="mt-4 block text-sm font-bold"
                >
                  PIN
                </label>
                <input
                  id="student-join-pin"
                  aria-label="PIN"
                  value={pin}
                  onChange={(event) => setPin(cleanPin(event.target.value))}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") submit();
                  }}
                  inputMode="numeric"
                  placeholder={t("studentHub.pinPlaceholder")}
                  className="mt-2 w-full rounded-2xl border-2 border-slate-200 px-4 py-3 text-lg font-bold tracking-[.18em] outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
                />
              </>
            )}
            {error && (
              <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-800">
                {error}
              </p>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={joining}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 font-black text-white disabled:opacity-60"
            >
              {joining ? t("studentHub.joining") : t("studentHub.join")}
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}
      {globalUpdates && <StudentClassUpdatesDialog open groups={globalUpdates.groups} title="Upcoming class events" emptyText="No upcoming events." onClose={() => { setGlobalUpdates(null); window.setTimeout(() => globalUpdateBell.current?.focus(), 0); }} />}
      {classUpdates && (
        <StudentClassUpdatesDialog
          open
          className={classUpdates.item.displayName || classUpdates.item.className || classUpdates.item.classCode}
          events={events[classUpdates.item.classCode] || []}
          title="Upcoming events"
          emptyText="No upcoming events."
          onClose={() => { setClassUpdates(null); window.setTimeout(() => classUpdateBell.current?.focus(), 0); }}
        />
      )}
    </div>
  );
}
